import Booth from './booth.js';
import _ from './translate.js';
import Presentation from './presentation.js';
import nav from './navigate.js';
import UploadStep from './uploadStep.js';
import Size from './size.js';
import Retouch from './retouch.js';
import TextStep from './textStep.js';
import SelectProduct from './selectProduct.js';
import GotoProduct from './gotoProduct.js';
import CropImageViewport from './cropImageViewport.js';
import CropImageToolbar from './cropImageToolbar.js';
import ZoomPanToolbox from './ZoomPanToolbox.js';
import StartOver from './startover.js';
import Paper from './paper.js';
import events from './event.js';
import imageHelper from './imageHelper.js';
import Logger from './logger.js';
import Storage from './storage.js';

class Customizer {
    popupContentDiv = null; // The content div of the popup
    wrapperDiv = null;
    contentDiv = null;
    cropperDiv = null;
    cropperToolbarDiv = null; // Toolbar for the cropper
    cropper = null;
    cropperPreviewDiv = null;
    cropperToolbar = null;
    zoomPanToolbox = null;
    topNavigationDiv = null;
    bottomNavigationDiv = null;
    options = null; // Options for the customizer
    isReady = false; // Flag to indicate if customizer is fully initialized
    readyPromise = null; // Promise that resolves when customizer is ready

    constructor(popupContentDiv, options = null) {
        this.popupContentDiv = popupContentDiv;
        if (!this.popupContentDiv) {
            Logger.error('Customizer constructor loading failed.');
            return;
        }
        this.options = options || {}; // Store options for later use
        this.readyPromise = this.init();
    }

    async init() { // Initialize the customizer

        // create cropper wrapper div
        this.cropperDiv = document.createElement('div');
        this.cropperDiv.classList.add('mscp-crop-wrapper');
        this.popupContentDiv.appendChild(this.cropperDiv);

        // create cropper preview div
        this.cropperPreviewDiv = document.createElement('div');
        this.cropperPreviewDiv.classList.add('mscp-crop-preview');
        this.cropperDiv.appendChild(this.cropperPreviewDiv);

        // create cropper toolbar
        this.cropperToolbarDiv = document.createElement('div');
        this.cropperToolbarDiv.classList.add('mscp-crop-toolbar');
        this.cropperDiv.appendChild(this.cropperToolbarDiv);


        // outer content wrapper div
        this.wrapperDiv = document.createElement('div');
        this.wrapperDiv.classList.add('mscp-wrapper');
        // this.wrapperDiv.dataset.activeStep = 'customizer';
        this.wrapperDiv.innerHTML = "";
        this.popupContentDiv.appendChild(this.wrapperDiv);

        // create top navigation
        this.topNavigationDiv = document.createElement('div');
        this.topNavigationDiv.classList.add('mscp-top-navigation-wrapper');
        // this.wrapperDiv.appendChild(this.topNavigationDiv);

        // create content div
        this.contentDiv = document.createElement('div');
        this.contentDiv.classList.add('mscp-content-wrapper');
        this.wrapperDiv.appendChild(this.contentDiv);

        // create bottom navigation
        this.bottomNavigationDiv = document.createElement('div');
        this.bottomNavigationDiv.classList.add('mscp-bottom-navigation-wrapper');
        this.wrapperDiv.appendChild(this.bottomNavigationDiv);


        this.cropper = new CropImageViewport(this.cropperPreviewDiv, {
            ...this.options,
        });
        this.cropperToolbar = new CropImageToolbar(this.cropperToolbarDiv, {
            ...this.options,
        });

        this.zoomPanToolbox = new ZoomPanToolbox(this.cropper, this.contentDiv);

        await this.initPopup();

        // Mark as ready AFTER everything is initialized
        this.isReady = true;
        events.trigger('customizerReady');

        events.add('stepChanged', (step) => { this.activateLoading(); }); // Reset adjust mode when changing steps
    }

    activateLoading() {
        this.popupContentDiv.classList.add('loading');
    }

    deactivateLoading() {
        this.popupContentDiv.classList.remove('loading');
    }

    /**
     * Determines which step to show based on current state and options.
     * This is used both on initial load and when reopening the popup.
     */
    async determineInitialStep(options = {}) {
        // Wait until customizer is fully ready
        if (!this.isReady) {
            await this.readyPromise;
        }

        // Fotobås prototype: its own step 1 covers the popup (js/module/booth.js)
        if (options.fotobas) {
            events.trigger('openFotobas');
            return;
        }
        events.trigger('closeFotobas');

        // in edit mode, go to paper step directly
        // if (options.openInEdit) {
        //     events.trigger('changeStep', 'paper');
        // }
        // // not in edit mode, but image already uploaded
        // else 
        if (await imageHelper.getActiveImage()) {
            // Shop card clicked with the visitor's image already uploaded: skip upload, start-over dialog
            // and (when the look names a presentation) the presentation step.
            if (options.useExistingImage && options.paper) {
                // Not awaited: on the first open the step modules are built after this method returns.
                this.openWithExistingImage(options).then((opened) => {
                    if (!opened) {
                        events.trigger('openStartOver');
                        events.trigger('setSize', Storage.get('size'));
                    }
                });
                return;
            }
            // Check if we have an active image
            if (Storage.get('activeImageID')) {
                // events.trigger('changeStep', 'paper');
                events.trigger('openStartOver');
                events.trigger('setSize', Storage.get('size'));
            }
        }
        // No image uploaded yet and not in edit mode
        else {
            await events.trigger('reInitUpload'); // async listeners reset the list; the preselect must come after
            // Shop deep link on a reopened popup (first open: UploadStep gets options.paper)
            // Also without a paper: clears the product of an earlier shop open.
            events.trigger('preselectUploadPaper', options.paper || null, options.presentation || null);
            // events.trigger('showLoadingOverlay', 'paper');
        }
    }



    /**
     * Opens the popup past the upload step for an already uploaded image: selects the paper (and the
     * presentation when valid) exactly as the deep link does after an upload, then goes to the step that
     * follows: size when both are chosen, otherwise presentation. Mirrors Navigate's
     * imageUploadStatusChanged handler. Resolves false when the paper could not be selected (the caller
     * then falls back to the start-over dialog).
     */
    async openWithExistingImage(options) {
        if (!await events.waitFor('preselectUploadPaper')) return false;
        // Choices made for an earlier design do not carry over to this look (the session is kept, unlike
        // after "Start new design"): the look decides presentation, and the visitor picks the size.
        Storage.remove('size');
        Storage.remove('presentation');
        Storage.remove('uploadPresentation');
        await events.trigger('preselectUploadPaper', options.paper, options.presentation || null);
        if (Storage.get('uploadPaper') !== options.paper) return false;

        let step = 'presentation';
        const wanted = Storage.get('uploadPresentation') || (await events.trigger('getShopPresentation')).find(Boolean);
        if (wanted && await events.waitFor('applyPresentationPreselect')) {
            const applied = (await events.trigger('applyPresentationPreselect', wanted)).includes(true);
            if (applied) step = 'size';
        }
        events.trigger('changeStep', step);
        return true;
    }

    async initPopup() {
        new StartOver(this.popupContentDiv, this.options); // Initialize the start over module
        nav.init(this.topNavigationDiv, this.bottomNavigationDiv, this.popupContentDiv); // Initialize the navigator module
        // upload.init(this.contentDiv, this.cropperPreviewDiv); // Initialize the upload module

        // Add event listener for determining initial step
        events.add('determineInitialStep', async (options) => {
            await this.determineInitialStep(options || this.options);
        });

        // Determine which step to show on initial load
        new Booth(this.popupContentDiv); // listens for openFotobas / closeFotobas
        await this.determineInitialStep(this.options);

        // do following in other thread to avoid blocking the UI
        setTimeout(() => {
            new UploadStep(this.contentDiv, this.cropperDiv, this.options?.paper || null, this.options?.presentation || null); // Pass cropperDiv so upload button can be positioned correctly
            // När en bild sparas lokalt, visa upload overlay tills den är uppladdad
            events.add('newImageLocalSaved', ({ status }) => {
                // Visa upload overlay under uppladdning
                events.trigger('showLoadingOverlay', 'upload');
                Logger.debug('Image saved locally, showing upload overlay. Status:', status);
            });
            new Presentation(this.contentDiv, this.options);
            new Paper(this.contentDiv); // Initialize the paper module
            new Size(this.contentDiv); // Initialize the size module
            new TextStep(this.contentDiv); // Text step of the graphic looks
            new Retouch(this.contentDiv); // Initialize the retouch module
            new SelectProduct(this.contentDiv); // Initialize the select product module
            new GotoProduct(this.contentDiv); // Initialize the goto product module
        }, 0);
    }

}

export default Customizer;